import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MessageCircle } from "lucide-react";
import { useApp } from "../contexts/AppContext";

// TikTok ikonası lucide-react-də olmadığına görə kiçik inline SVG istifadə edirik.
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.91a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z"/>
  </svg>
);

export default function Footer() {
  const { tx, t, content } = useApp();
  const tcompany = t.footer.company || "ŞİRKƏT";
  const tcompanyLinks = t.footer.companyLinks || {
    about: "Haqqımızda",
    blog: "Yeniliklər",
    privacy: "Məxfilik siyasəti",
  };

  // CMS-dən brand adı və sosial linklər (default-lar saxlanır).
  const brandName = tx("brand.name") || "VINYL BAKU";
  const social = content?.social || {};

  // URL-i normalaşdır: tel:, mailto:, http(s):// və ya // ilə başlamırsa,
  // https:// prefiksi əlavə et ki browser nisbi yol kimi qəbul etməsin.
  const normalizeUrl = (raw) => {
    if (!raw) return "";
    const v = String(raw).trim();
    if (!v) return "";
    if (/^(https?:)?\/\//i.test(v)) return v;
    if (/^(tel:|mailto:)/i.test(v)) return v;
    return `https://${v.replace(/^\/+/, "")}`;
  };

  // Sosial ikonların massivi — yalnız dolu olanları göstəririk.
  const socials = [
    { key: "instagram_url", url: normalizeUrl(social.instagram_url), label: "Instagram",  icon: <Instagram size={16} />, external: true },
    { key: "tiktok_url",    url: normalizeUrl(social.tiktok_url),    label: "TikTok",     icon: <TikTokIcon size={16} />, external: true },
    { key: "whatsapp_url",  url: normalizeUrl(social.whatsapp_url),  label: "WhatsApp",   icon: <MessageCircle size={16} />, external: true },
    { key: "phone",         url: social.phone ? `tel:${social.phone}` : "",   label: social.phone, icon: <Phone size={16} />, external: false },
    { key: "email",         url: social.email ? `mailto:${social.email}` : "", label: social.email, icon: <Mail size={16} />, external: false },
  ].filter((s) => Boolean(s.url));

  return (
    <footer className="border-t border-[var(--border)] mt-32" data-testid="site-footer">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="font-display text-2xl mb-3">{brandName}</div>
          <p className="text-[var(--text-2)] text-sm leading-relaxed whitespace-pre-line max-w-md">{tx("footer.about")}</p>

          {/* Sosial linklər — yalnız admin paneldən dolduruldusa görsənir */}
          {socials.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="footer-socials">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target={s.external ? "_blank" : undefined}
                  rel={s.external ? "noopener noreferrer" : undefined}
                  aria-label={s.label}
                  title={s.label}
                  className="inline-flex items-center justify-center text-[var(--text-2)] hover:text-[var(--primary)] transition-colors p-1"
                  data-testid={`footer-social-${s.key}`}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="metadata mb-4">{tcompany}</div>
          <ul className="space-y-2 text-sm text-[var(--text-2)]">
            <li><Link to="/catalog" className="hover:text-[var(--primary)]">{t.footer.shopLinks.newArrivals}</Link></li>
            <li><Link to="/catalog" className="hover:text-[var(--primary)]">{t.footer.shopLinks.catalog}</Link></li>
            <li><Link to="/artists" className="hover:text-[var(--primary)]">{t.footer.shopLinks.artists}</Link></li>
            <li className="text-[var(--text-muted)]">{t.footer.shopLinks.preorders}</li>
          </ul>
        </div>
        <div>
          <div className="metadata mb-4">{t.footer.shop}</div>
          <ul className="space-y-2 text-sm text-[var(--text-2)]">
            <li><Link to="/about" className="hover:text-[var(--primary)]" data-testid="footer-about">{tcompanyLinks.about}</Link></li>
            <li><Link to="/blog" className="hover:text-[var(--primary)]" data-testid="footer-blog">{tcompanyLinks.blog}</Link></li>
            <li><Link to="/privacy" className="hover:text-[var(--primary)]" data-testid="footer-privacy">{tcompanyLinks.privacy}</Link></li>
          </ul>
        </div>
        <div>
          <div className="metadata mb-4">{t.footer.support}</div>
          <ul className="space-y-2 text-sm text-[var(--text-2)]">
            <li className="text-[var(--text-muted)]">{t.footer.supportLinks.shipping}</li>
            <li className="text-[var(--text-muted)]">{t.footer.supportLinks.returns}</li>
            <li className="text-[var(--text-muted)]">{t.footer.supportLinks.faq}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[var(--border-soft)]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-5 flex flex-wrap gap-3 justify-between metadata">
          <span>{tx("footer.copyright")}</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[var(--primary)]">{tcompanyLinks.privacy}</Link>
            <span className="text-[var(--text-muted)]">{tx("footer.tagline")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
